import { useEffect, useState } from "react";
import map from "lodash/map";
import reduce from "lodash/reduce";
import { LinkedAccount } from "../../../../../models/Entities";
import { fetchCommonModelsFromCategoryForIntegration } from "../../../../../api-client/categories/CommonModelTogglesAPIClient";
import useDeprecatedCommonModel from "../../../../../hooks/useDeprecatedCommonModel";
import {
  createSortedModelsMap,
  isEnabledCommonModel,
} from "../../../configuration/common-models/CommonModelUtils";

type UseCategoryCommonModelsProps = {
  linkedAccount: LinkedAccount;
};

/**
 * Fetches all common models for a given category, filtering out deprecated ones.
 *
 * Returns categoryCommonModels (the full supported list) and enabledCommonModels (the subset usable
 * for this Linked Account: endpoint-backed, scopes enabled, not a custom object, not permanently
 * disabled), which populates the Field Mapping "filter by Common Model" dropdown.
 */
const useCategoryCommonModels = ({ linkedAccount }: UseCategoryCommonModelsProps) => {
  // state
  const [categoryCommonModels, setCategoryCommonModels] = useState<Array<string>>([]);
  const [enabledCommonModels, setEnabledCommonModels] = useState<Array<string>>([]);

  // hooks
  const { filterDeprecatedCommonModels, filterDeprecatedFields } = useDeprecatedCommonModel({
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    category: linkedAccount?.category!,
  });

  useEffect(() => {
    if (linkedAccount) {
      fetchCommonModelsFromCategoryForIntegration(
        linkedAccount.category,
        linkedAccount.integration,
        linkedAccount.id,
      ).then((result) => {
        if (result.status === "success") {
          // filter out common models and fields w/ deprecation stage SUNSET
          const supportedCommonModles = filterDeprecatedCommonModels(map(result.data, "name"));
          const filteredData = reduce(
            result.data,
            (acc, cur, index: number) => {
              if (supportedCommonModles.includes(cur.name)) {
                acc[index] = {
                  ...cur,
                  fields: cur.fields.filter((field) => {
                    const filteredFields = filterDeprecatedFields(
                      cur.name,
                      map(cur.fields, "field_name"),
                    );

                    return filteredFields.includes(field.field_name);
                  }),
                };
              }

              return acc;
            },
            [] as any,
          );

          setCategoryCommonModels([...Object.keys(createSortedModelsMap(filteredData))]);

          const enabledModels = (result.data ?? [])
            .filter(
              (model) =>
                supportedCommonModles.includes(model.name) &&
                isEnabledCommonModel(model) &&
                !model.is_permanently_disabled,
            )
            .map((model) => model.name)
            .sort((a, b) => a.localeCompare(b));
          setEnabledCommonModels(enabledModels);
        }
      });
    }
  }, [filterDeprecatedCommonModels, filterDeprecatedFields, linkedAccount]);

  return {
    categoryCommonModels,
    enabledCommonModels,
  };
};

export default useCategoryCommonModels;
